import React, { useState, useEffect } from 'react';
import { FileCheck2, Plus, Search, Calendar, AlertTriangle, AlertOctagon, CheckCircle, Upload, Trash2, Download, Eye, X, Building2, Filter, FileText, Briefcase, Edit2, Sparkles } from 'lucide-react';
import ClearFiltersButton from './ClearFiltersButton';

import { OfficialDocument, DocType, AnalyzedFile, CompanyProfile } from '../types';
import { logActivity } from '../services/auditService';
import { apiService } from '../services/apiService';
import useCompanies from '../hooks/useCompanies';

import { useModal } from './ModalProvider';

const DocumentGuide: React.FC = () => {
    const [docs, setDocs] = useState<OfficialDocument[]>([]);
    const { companies, loading: companiesLoading, refresh: refreshCompanies, getCompanyName } = useCompanies();

  // Filtros
  const [searchTerm, setSearchTerm] = useState('');
  const [filterType, setFilterType] = useState<DocType | 'ALL'>('ALL');
  const [companyFilter, setCompanyFilter] = useState<string>('ALL');
  const [statusFilter, setStatusFilter] = useState<'ALL' | 'VALID' | 'WARNING' | 'EXPIRED'>('ALL'); // Novo Filtro de Status

  // Modal State
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingDoc, setEditingDoc] = useState<OfficialDocument | null>(null);
  const [form, setForm] = useState<Partial<OfficialDocument>>({
    title: '',
    type: 'FEDERAL',
    issuer: '',
    issueDate: '',
    expirationDate: '',
    companyId: ''
  });
  const [selectedFile, setSelectedFile] = useState<File | null>(null);

  // Estados para IA
  const [isExtracting, setIsExtracting] = useState(false);
  const [aiSuccess, setAiSuccess] = useState(false);

  // Helper robusto para criar datas locais sem interferência de fuso horário UTC
  const parseLocalDate = (dateString: string): Date => {
    if (!dateString || dateString === '') {
      return new Date('Invalid');
    }

    // Se for ISO completo (2026-02-13T00:00:00.000Z), extrair só a data
    const dateOnly = dateString.includes('T') ? dateString.split('T')[0] : dateString;

    const [year, month, day] = dateOnly.split('-').map(Number);
    if (!year || !month || !day) {
      return new Date('Invalid');
    }
    return new Date(year, month - 1, day);
  };

  const calculateStatus = (expirationDate?: string): OfficialDocument['status'] => {
    if (!expirationDate) return 'NO_EXPIRATION';

    const today = new Date();
    today.setHours(0, 0, 0, 0);

    const exp = parseLocalDate(expirationDate);
    exp.setHours(0, 0, 0, 0);

    const diffTime = exp.getTime() - today.getTime();
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

    if (diffDays < 0) return 'EXPIRED';
    if (diffDays <= 15) return 'WARNING';
    return 'VALID';
  };

  const getDaysRemaining = (dateString?: string) => {
    if (!dateString) return null;

    const today = new Date();
    today.setHours(0, 0, 0, 0);

    const exp = parseLocalDate(dateString);

    // Verificar se a data é válida
    if (isNaN(exp.getTime())) {
      console.error('❌ Data inválida em getDaysRemaining:', dateString);
      return null;
    }

    exp.setHours(0, 0, 0, 0);

    const diffTime = exp.getTime() - today.getTime();
    return Math.ceil(diffTime / (1000 * 60 * 60 * 24));
  };

  useEffect(() => {
    // Carregar Documentos
    const loadDocs = async () => {
      try {
                const data = await apiService.getDocuments();
                // Além dos documentos oficiais, também buscar atestados migrados na nova API
                let tech: any[] = [];
                try { tech = await apiService.getTechnicalCertificates(); } catch (e) { console.debug('Não foi possível carregar technical certificates:', e); }
                // Mapear technical certificates para o shape esperado (incluir type='ATESTADO')
                const mappedTech = tech.map(t => ({ ...t, type: 'ATESTADO' }));
                const merged = [...data, ...mappedTech];
        if (data.length === 0) {
           const stored = localStorage.getItem('licitaflow_official_docs');
           if (stored) {
             try {
               const initialData = JSON.parse(stored);
               if (Array.isArray(initialData) && initialData.length > 0) {
                 console.log("Migrando Documentos do LocalStorage...");
                 for (const d of initialData) {
                   const { id, ...docData } = d;
                   await apiService.createDocument(docData);
                 }
                 localStorage.removeItem('licitaflow_official_docs');
                 setDocs(await apiService.getDocuments());
                 return;
               }
             } catch (e) { console.error(e); }
           }
        }
                setDocs(merged);
      } catch (err) {
        console.error('Erro ao carregar documentos:', err);
      }
    };
    loadDocs();

        // companies are loaded via shared hook; nothing to do here
  }, []);

  // Debug: Log quando form mudar
  useEffect(() => {
    console.log('🔄 Estado do form atualizado:', form);
  }, [form]);

  const handleFileUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      setSelectedFile(e.target.files[0]);
      setAiSuccess(false); // Reset status de IA se o usuário trocar o arquivo manualmente
    }
  };

    const clearFilters = () => {
        setSearchTerm('');
        setFilterType('ALL');
        setCompanyFilter('ALL');
        setStatusFilter('ALL');
    };

  // Função de Extração Automática via IA
  const handleAiExtraction = async (file: File) => {
    setIsExtracting(true);

    try {
      // Converter arquivo para base64
      const base64 = await new Promise<string>((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => {
          const result = reader.result as string;
          const base64String = result.split(',')[1]; // Remove o prefixo "data:application/pdf;base64,"
          resolve(base64String);
        };
        reader.onerror = reject;
        reader.readAsDataURL(file);
      });

      // Chama a API para extrair dados do PDF
      const extractedData = await apiService.extractCertificateData(base64, file.name);

      console.log('📊 Dados extraídos da API:', extractedData);
      console.log('📅 Datas recebidas:', {
        issue_date: extractedData.issue_date,
        expiration_date: extractedData.expiration_date
      });

      // Converter datas de DD/MM/AAAA para AAAA-MM-DD (formato do input date)
      const convertDate = (dateStr: string) => {
        if (!dateStr || dateStr === 'NÃO CONSTA') return '';
        const parts = dateStr.split('/');
        if (parts.length === 3) {
          const converted = `${parts[2]}-${parts[1]}-${parts[0]}`;
          console.log(`📅 Convertendo data: ${dateStr} → ${converted}`);
          return converted;
        }
        return '';
      };

      // Mapear tipo extraído para o enum do sistema
      const mapType = (type: string): DocType => {
        const typeMap: Record<string, DocType> = {
          'Federal': 'FEDERAL',
          'Estadual': 'ESTADUAL',
          'Municipal': 'MUNICIPAL',
          'Trabalhista': 'TRABALHISTA',
          'Ambiental': 'OUTROS',
          'Técnica': 'ATESTADO',
          'Outro': 'OUTROS'
        };
        return typeMap[type] || 'OUTROS';
      };

      // Preenche o formulário com os dados extraídos
      setForm(prev => {
        const newFormData = {
          ...prev,
          title: extractedData.title && extractedData.title !== 'NÃO CONSTA'
            ? extractedData.title
            : (prev.title || file.name.replace(/\.(pdf|png|jpg|jpeg)$/i, '').replace(/[-_]/g, ' ')),
          type: mapType(extractedData.type),
          issuer: extractedData.issuing_agency && extractedData.issuing_agency !== 'NÃO CONSTA'
            ? extractedData.issuing_agency
            : prev.issuer,
          issueDate: convertDate(extractedData.issue_date) || prev.issueDate,
          expirationDate: convertDate(extractedData.expiration_date) || prev.expirationDate
        };

        console.log('📝 Preenchendo formulário:', newFormData);

        return newFormData;
      });      setAiSuccess(true);
      await showAlert("Dados extraídos com sucesso! Revise e clique em Salvar.");
    } catch (error) {
      console.error('Erro ao extrair dados do PDF:', error);
      // Fallback: preenche apenas o título com o nome do arquivo
      setForm(prev => ({
        ...prev,
        title: prev.title || file.name.replace(/\.(pdf|png|jpg|jpeg)$/i, '').replace(/[-_]/g, ' ')
      }));
      await showAlert("Não foi possível extrair dados automaticamente. Preencha os campos manualmente.");
    } finally {
      setIsExtracting(false);
    }
  };

  const onDropFile = async (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      const file = e.dataTransfer.files[0];
      const validTypes = ['application/pdf', 'image/png', 'image/jpeg', 'image/jpg'];
      if (validTypes.includes(file.type)) {
        setSelectedFile(file);
        handleAiExtraction(file); // Gatilho automático ao soltar
      } else {
        await showAlert("Apenas arquivos PDF ou imagens (PNG/JPG) são aceitos.");
      }
    }
  };



  const handleOpenModal = (doc?: OfficialDocument) => {
      // Tentar pré-selecionar uma empresa se estiver filtrando ou se houver padrão
      let defaultCompanyId = '';
      if (companyFilter !== 'ALL') {
          defaultCompanyId = companyFilter;
      } else if (companies.length > 0) {
          const defaultComp = companies.find(c => c.isDefault);
          defaultCompanyId = defaultComp ? defaultComp.id : companies[0].id;
      }

      if (doc) {
          setEditingDoc(doc);
          setForm({
              title: doc.title,
              type: doc.type,
              issuer: doc.issuer,
              issueDate: doc.issueDate,
              expirationDate: doc.expirationDate,
              companyId: doc.companyId || defaultCompanyId
          });
      } else {
          setEditingDoc(null);
          setForm({
              title: '',
              type: 'FEDERAL',
              issuer: '',
              issueDate: '',
              expirationDate: '',
              companyId: defaultCompanyId
          });
      }
      setSelectedFile(null);
      setIsExtracting(false);
      setAiSuccess(false);
      setIsModalOpen(true);
  };

  const handleSave = async () => {
    if (!form.title || !form.type) {
                await showAlert("Título e Tipo são obrigatórios.");
        return;
    }

    const finalCompanyId = form.companyId || (companies.length > 0 ? companies[0].id : undefined);
    let finalFileUrl = editingDoc?.fileUrl;

    try {
        if (selectedFile) {
            // 1. Gerar Key única
            const fileExt = selectedFile.name.split('.').pop();
            const key = `documents/${finalCompanyId}/${globalThis.crypto?.randomUUID?.() || (Date.now().toString(36) + Math.random().toString(36).substr(2))}.${fileExt}`;

            // 2. Obter URL de Upload
            const { uploadUrl } = await apiService.getPresignedUploadUrl(key, selectedFile.type);

            // 3. Fazer Upload direto para o S3
            await apiService.uploadFileToS3(uploadUrl, selectedFile);

            finalFileUrl = key;
        }

        const payload = {
            ...form,
            companyId: finalCompanyId,
            status: calculateStatus(form.expirationDate),
            fileUrl: finalFileUrl
        };

        console.log('💾 Salvando documento com payload:', payload);

        if (editingDoc) {
            await apiService.updateDocument(editingDoc.id, payload);
            logActivity('DOCUMENTO_EDICAO', `Documento editado: ${form.title}`, 'INFO');
        } else {
            await apiService.createDocument(payload);
            const companyName = companies.find(c => c.id === finalCompanyId)?.tradeName || 'Geral';
            logActivity('DOCUMENTO_CRIACAO', `Documento adicionado: ${form.title} (${companyName})`, 'SUCCESS');
        }
        setDocs(await apiService.getDocuments());
        setIsModalOpen(false);
    } catch (error: any) {
        console.error(error);
        await showAlert("Erro ao salvar documento: " + (error.message || error));
    }
  };

    const handleDelete = async (id: string, title: string) => {
        const ok = await showConfirm(`Excluir documento "${title}"?`);
        if (!ok) return;
        try {
                await apiService.deleteDocument(id);
                setDocs(await apiService.getDocuments());
                logActivity('DOCUMENTO_EXCLUSAO', `Documento excluído: ${title}`, 'WARNING');
        } catch (error) {
                console.error(error);
                await showAlert("Erro ao excluir documento.");
        }
    };

  const handleDownload = async (doc: OfficialDocument) => {
    try {
        if (doc.fileUrl) {
            const { downloadUrl } = await apiService.getPresignedDownloadUrl(doc.fileUrl);
            window.open(downloadUrl, '_blank');
            return;
        }

        if (doc.file && doc.file.base64) {
            const link = document.createElement('a');
            link.href = `data:${doc.file.type};base64,${doc.file.base64}`;
            link.download = doc.file.name;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            return;
        }

        await showAlert("Nenhum arquivo anexado.");
    } catch (e) {
        console.error(e);
        await showAlert("Erro ao baixar arquivo.");
    }
  };

  const { showAlert, showConfirm } = useModal();

  const filteredDocs = docs.filter(doc => {
    const matchesSearch = doc.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
                          doc.issuer.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesType = filterType === 'ALL' || doc.type === filterType;
    const matchesCompany = companyFilter === 'ALL' || doc.companyId === companyFilter;
    const matchesStatus = statusFilter === 'ALL' || doc.status === statusFilter;

    return matchesSearch && matchesType && matchesCompany && matchesStatus;
  });

    // getCompanyName provided by useCompanies hook

  const getStatusBadge = (doc: OfficialDocument) => {
    const days = getDaysRemaining(doc.expirationDate);

    switch (doc.status) {
        case 'EXPIRED':
            return (
                <div className="flex items-center gap-1.5 text-red-700 dark:text-red-300 bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-800/60 px-2 py-1 rounded-md text-xs font-bold animate-pulse">
                    <AlertOctagon className="w-3.5 h-3.5" />
                    VENCIDO há {Math.abs(days || 0)} dias
                </div>
            );
        case 'WARNING':
            return (
                <div className="flex items-center gap-1.5 text-amber-700 bg-amber-50 border border-amber-200 px-2 py-1 rounded-md text-xs font-bold">
                    <AlertTriangle className="w-3.5 h-3.5" />
                    Vence em {days ?? 0} dias
                </div>
            );
        case 'VALID':
            return (
                <div className="flex items-center gap-1.5 text-green-700 dark:text-green-300 bg-green-50 dark:bg-green-900/30 border border-green-200 dark:border-green-800/60 px-2 py-1 rounded-md text-xs font-medium">
                    <CheckCircle className="w-3.5 h-3.5" />
                    Válido ({days ?? 0} dias)
                </div>
            );
        default:
            return (
                <div className="flex items-center gap-1.5 text-ink-subtitle dark:text-ink-subtitle-dark bg-surface-alt dark:bg-surface-dark-alt border border-line dark:border-line-dark px-2 py-1 rounded-md text-xs font-medium">
                    <CheckCircle className="w-3.5 h-3.5" />
                    Sem Validade
                </div>
            );
    }
  };

  return (
    <div className="space-y-6 pb-8 max-w-full overflow-x-hidden">
      {/* Header */}
      <div className="bg-surface dark:bg-surface-dark rounded-xl shadow-sm border border-line dark:border-line-dark p-4 md:p-6">
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
          <div>
            <h2 className="text-lg font-semibold text-ink-title dark:text-ink-title-dark flex items-center gap-2">
              <FileCheck2 className="w-5 h-5 text-blue-600 dark:text-blue-300" />
              Guia de Documentos & Certidões
            </h2>
            <p className="text-sm text-ink-subtitle dark:text-ink-subtitle-dark mt-1">
              Monitore a validade de certidões e documentos obrigatórios para licitações.
            </p>
          </div>
                    <div className="flex items-center gap-2">
                        <button
                            onClick={() => handleOpenModal()}
                            className="w-full md:w-auto flex items-center justify-center gap-2 px-4 py-2 bg-brand-500 text-white rounded-lg hover:bg-brand-600 font-medium transition-colors shadow-sm"
                        >
                            <Plus className="w-4 h-4" />
                            Adicionar Documento
                        </button>
                        <ClearFiltersButton onClear={clearFilters} />
                    </div>
        </div>

        {/* Filtros em Grid */}
        <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 md:gap-4">

            {/* Filtro de Empresa */}
            <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <Briefcase className="h-4 w-4 text-ink-placeholder dark:text-ink-placeholder-dark" />
                </div>
                <select
                    value={companyFilter}
                    onChange={(e) => setCompanyFilter(e.target.value)}
                    className="w-full bg-blue-50 dark:bg-blue-900/30/50 border border-blue-100 text-ink-text dark:text-ink-text-dark text-sm rounded-lg focus:ring-brand-500 focus:border-brand-500 block pl-10 p-2.5 outline-none cursor-pointer font-medium"
                >
                    <option value="ALL">Empresa: Todas</option>
                    {companies.map(comp => (
                        <option key={comp.id} value={comp.id}>{comp.tradeName}</option>
                    ))}
                </select>
            </div>

            {/* Busca */}
            <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <Search className="h-4 w-4 text-ink-placeholder dark:text-ink-placeholder-dark" />
                </div>
                <input
                    type="text"
                    placeholder="Buscar por nome ou emissor..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="w-full bg-surface-alt dark:bg-surface-dark-alt border border-line dark:border-line-dark text-ink-text dark:text-ink-text-dark text-sm rounded-lg focus:ring-brand-500 focus:border-brand-500 block pl-10 p-2.5 outline-none"
                />
            </div>

            {/* Tipo */}
            <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <Filter className="h-4 w-4 text-ink-placeholder dark:text-ink-placeholder-dark" />
                </div>
                <select
                    value={filterType}
                    onChange={(e) => setFilterType(e.target.value as any)}
                    className="w-full bg-surface-alt dark:bg-surface-dark-alt border border-line dark:border-line-dark text-ink-text dark:text-ink-text-dark text-sm rounded-lg focus:ring-brand-500 focus:border-brand-500 block pl-10 p-2.5 outline-none cursor-pointer"
                >
                    <option value="ALL">Tipo: Todos</option>
                    <option value="FEDERAL">Federal</option>
                    <option value="ESTADUAL">Estadual</option>
                    <option value="MUNICIPAL">Municipal</option>
                    <option value="TRABALHISTA">Trabalhista</option>
                    <option value="FGTS">FGTS</option>
                    <option value="FALENCIA">Falência/Concordata</option>
                    <option value="BALANCO">Balanço/Contábil</option>
                    <option value="ATESTADO">Atestado Técnico</option>
                    <option value="OUTROS">Outros</option>
                </select>
            </div>

            {/* Status (NOVO) */}
            <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <AlertTriangle className="h-4 w-4 text-ink-placeholder dark:text-ink-placeholder-dark" />
                </div>
                <select
                    value={statusFilter}
                    onChange={(e) => setStatusFilter(e.target.value as any)}
                    className={`w-full border text-sm rounded-lg focus:ring-brand-500 focus:border-brand-500 block pl-10 p-2.5 outline-none cursor-pointer font-medium ${
                        statusFilter === 'EXPIRED' ? 'bg-red-50 dark:bg-red-900/30 border-red-200 dark:border-red-800/60 text-red-700 dark:text-red-300' :
                        statusFilter === 'WARNING' ? 'bg-amber-50 border-amber-200 text-amber-700' :
                        'bg-surface-alt dark:bg-surface-dark-alt border-line dark:border-line-dark text-ink-text dark:text-ink-text-dark'
                    }`}
                >
                    <option value="ALL">Status: Todos</option>
                    <option value="VALID">✅ Válidos</option>
                    <option value="WARNING">⚠️ Vencendo (15 dias)</option>
                    <option value="EXPIRED">❌ Vencidos</option>
                </select>
            </div>
        </div>
      </div>

      {/* Grid de Documentos */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6">
        {filteredDocs.map((doc) => (
            <div key={doc.id} className="bg-surface dark:bg-surface-dark rounded-xl shadow-sm border border-line dark:border-line-dark hover:shadow-md transition-all group relative overflow-hidden flex flex-col">
                {/* Faixa lateral de status */}
                <div className={`absolute left-0 top-0 bottom-0 w-1 ${
                    doc.status === 'EXPIRED' ? 'bg-red-500' :
                    doc.status === 'WARNING' ? 'bg-amber-500' :
                    doc.status === 'VALID' ? 'bg-green-500' : 'bg-gray-300'
                }`}></div>

                <div className="p-5 pl-6 flex-1">
                    <div className="flex justify-between items-start mb-2">
                        <span className="text-[10px] font-bold text-ink-placeholder dark:text-ink-placeholder-dark uppercase tracking-wider bg-surface-alt dark:bg-surface-dark-alt px-2 py-0.5 rounded border border-line dark:border-line-dark">
                            {doc.type}
                        </span>
                        <div className="flex gap-1">
                            <button
                                onClick={() => handleOpenModal(doc)}
                                className="p-1.5 text-ink-placeholder dark:text-ink-placeholder-dark hover:text-blue-600 rounded bg-surface-alt dark:bg-surface-dark-alt hover:bg-blue-50 transition-colors opacity-0 group-hover:opacity-100"
                                title="Editar"
                            >
                                <Edit2 className="w-4 h-4" />
                            </button>
                            <button onClick={() => handleDelete(doc.id, doc.title)} className="p-1.5 text-ink-placeholder dark:text-ink-placeholder-dark hover:text-red-600 rounded bg-surface-alt dark:bg-surface-dark-alt hover:bg-red-50 transition-colors opacity-0 group-hover:opacity-100" title="Excluir">
                                <Trash2 className="w-4 h-4" />
                            </button>
                        </div>
                    </div>

                    <h3 className="font-semibold text-ink-title dark:text-ink-title-dark mb-1 leading-snug truncate" title={doc.title}>
                        {doc.title}
                    </h3>

                    <div className="flex items-center gap-1.5 text-xs text-ink-subtitle dark:text-ink-subtitle-dark mb-2">
                        <Building2 className="w-3.5 h-3.5" />
                        {doc.issuer}
                    </div>

                    {/* Badge da Empresa */}
                    <div className="flex items-center gap-1.5 text-xs text-blue-600 dark:text-blue-300 bg-blue-50 dark:bg-blue-900/30 px-2 py-1 rounded w-fit mb-4 border border-blue-100">
                        <Briefcase className="w-3 h-3" />
                        <span className="font-medium truncate max-w-[200px]">{getCompanyName(doc.companyId)}</span>
                    </div>

                    <div className="space-y-3 pt-3 border-t border-line dark:border-line-dark">
                        <div className="flex justify-between items-center">
                            {getStatusBadge(doc)}
                        </div>

                        <div className="flex justify-between text-xs text-ink-subtitle dark:text-ink-subtitle-dark">
                             <div className="flex flex-col">
                                <span className="text-[10px] uppercase text-ink-placeholder dark:text-ink-placeholder-dark font-bold">Validade</span>
                                <div className="flex items-center gap-1 mt-0.5">
                                    <Calendar className="w-3 h-3" />
                                    {doc.expirationDate && doc.expirationDate !== ''
                                      ? parseLocalDate(doc.expirationDate).toLocaleDateString('pt-BR')
                                      : 'Indefinida'}
                                </div>
                             </div>
                             {doc.issueDate && doc.issueDate !== '' && (
                                <div className="flex flex-col items-end">
                                    <span className="text-[10px] uppercase text-ink-placeholder dark:text-ink-placeholder-dark font-bold">Emissão</span>
                                    <span className="mt-0.5">{parseLocalDate(doc.issueDate).toLocaleDateString('pt-BR')}</span>
                                </div>
                             )}
                        </div>
                    </div>
                </div>

                {/* Rodapé do card - Botão de Download */}
                {(doc.file || doc.fileUrl) && (
                    <div className="px-5 py-3 bg-surface-alt dark:bg-surface-dark-alt border-t border-line dark:border-line-dark hover:bg-blue-50 transition-colors cursor-pointer" onClick={() => handleDownload(doc)}>
                        <div className="flex items-center gap-3">
                            <div className="p-2 bg-surface dark:bg-surface-dark rounded-lg border border-line dark:border-line-dark text-blue-600 dark:text-blue-300 shadow-sm">
                                <FileText className="w-4 h-4" />
                            </div>
                            <div className="flex-1 overflow-hidden">
                                <span className="text-xs font-medium text-ink-text dark:text-ink-text-dark truncate block w-full group-hover:text-blue-700">
                                    {doc.file ? doc.file.name : 'Documento Anexado'}
                                </span>
                                <span className="text-[10px] text-ink-placeholder dark:text-ink-placeholder-dark block mt-0.5">
                                    {doc.file ? `${(doc.file.size / 1024).toFixed(1)} KB • ` : ''}Clique para baixar
                                </span>
                            </div>
                            <Download className="w-4 h-4 text-ink-placeholder dark:text-ink-placeholder-dark group-hover:text-blue-600" />
                        </div>
                    </div>
                )}
            </div>
        ))}
      </div>

      {filteredDocs.length === 0 && (
          <div className="text-center py-12 text-ink-subtitle dark:text-ink-subtitle-dark bg-surface dark:bg-surface-dark rounded-xl border border-line dark:border-line-dark border-dashed">
              <FileCheck2 className="w-12 h-12 text-ink-placeholder dark:text-ink-placeholder-dark mx-auto mb-3" />
              <p>Nenhum documento encontrado.</p>
              {companies.length === 0 && (
                  <p className="text-sm text-red-500 dark:text-red-400 mt-2">Dica: Cadastre suas empresas no menu Configurações para organizar melhor.</p>
              )}
          </div>
      )}

      {/* Modal Adicionar/Editar */}
      {isModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm animate-fade-in">
            <div key={`${form.issueDate}-${form.expirationDate}-${aiSuccess}`} className="bg-surface dark:bg-surface-dark rounded-xl shadow-xl w-full max-w-lg overflow-hidden animate-scale-in flex flex-col max-h-[90vh]">
                <div className="p-4 border-b border-line dark:border-line-dark flex justify-between items-center bg-surface-alt dark:bg-surface-dark-alt flex-shrink-0">
                    <h3 className="font-semibold text-ink-title dark:text-ink-title-dark flex items-center gap-2">
                        <Upload className="w-4 h-4 text-blue-600 dark:text-blue-300" />
                        {editingDoc ? 'Editar Documento' : 'Novo Documento'}
                    </h3>
                    <button onClick={() => setIsModalOpen(false)} className="text-ink-placeholder dark:text-ink-placeholder-dark hover:text-ink-subtitle dark:text-ink-subtitle-dark">
                        <X className="w-5 h-5" />
                    </button>
                </div>

                <div className="p-6 space-y-4 overflow-y-auto flex-1">
                    {/* Seleção de Empresa */}
                    <div>
                        <label className="block text-xs font-medium text-ink-text dark:text-ink-text-dark mb-1">Empresa Vinculada *</label>
                        <select
                            className="w-full border border-line-strong dark:border-line-dark-strong rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-brand-500 outline-none bg-surface dark:bg-surface-dark"
                            value={form.companyId}
                            onChange={e => setForm({...form, companyId: e.target.value})}
                        >
                            <option value="" disabled>Selecione a empresa...</option>
                            {companies.map(comp => (
                                <option key={comp.id} value={comp.id}>{comp.tradeName} ({comp.cnpj})</option>
                            ))}
                        </select>
                        {companies.length === 0 && (
                            <p className="text-[10px] text-red-500 dark:text-red-400 mt-1">Nenhuma empresa cadastrada. Vá em Configurações.</p>
                        )}
                    </div>

                    {/* Área de Drag & Drop para Extração Automática via IA */}
                    {!editingDoc && (
                        <div
                            onDrop={onDropFile}
                            onDragOver={(e) => e.preventDefault()}
                            className={`
                                border-2 border-dashed rounded-xl transition-all
                                ${isExtracting ? 'border-purple-400 bg-purple-50 dark:bg-purple-900/30' : 'border-purple-300 bg-purple-50 dark:bg-purple-900/30/30 hover:bg-purple-50 dark:bg-purple-900/30'}
                            `}
                        >
                            {isExtracting ? (
                                <div className="flex flex-col items-center justify-center py-8">
                                    <div className="animate-spin rounded-full h-10 w-10 border-4 border-purple-200 border-t-purple-600 mb-3"></div>
                                    <p className="text-sm font-bold text-purple-800 dark:text-purple-200">Extraindo Dados com IA...</p>
                                    <p className="text-xs text-purple-600 dark:text-purple-300">Aguarde alguns instantes</p>
                                </div>
                            ) : aiSuccess ? (
                                <div className="flex flex-col items-center justify-center py-6">
                                    <CheckCircle className="w-8 h-8 text-green-600 dark:text-green-300 mb-2" />
                                    <p className="text-sm font-bold text-green-800 dark:text-green-200">Dados Extraídos com Sucesso!</p>
                                    <p className="text-xs text-green-600 dark:text-green-300">O formulário abaixo foi preenchido automaticamente.</p>
                                </div>
                            ) : (
                                <div className="flex flex-col items-center justify-center py-2">
                                    <div className="bg-surface dark:bg-surface-dark p-3 rounded-full shadow-sm mb-3">
                                        <Sparkles className="w-6 h-6 text-purple-600 dark:text-purple-300" />
                                    </div>
                                    <h4 className="font-bold text-purple-900 mb-1">Preenchimento Automático via IA</h4>
                                    <p className="text-xs text-purple-700 max-w-xs mx-auto mb-3">
                                        Arraste o PDF da Certidão/Documento aqui para que a IA preencha o cadastro e anexe o arquivo automaticamente.
                                    </p>
                                    <label className="text-xs bg-purple-600 text-white px-3 py-1.5 rounded-lg hover:bg-purple-700 transition-colors cursor-pointer shadow-sm">
                                        Selecionar PDF
                                        <input
                                            type="file"
                                            accept=".pdf,.png,.jpg,.jpeg"
                                            className="hidden"
                                            onChange={(e) => {
                                                if (e.target.files && e.target.files[0]) {
                                                    setSelectedFile(e.target.files[0]);
                                                    handleAiExtraction(e.target.files[0]);
                                                }
                                            }}
                                        />
                                    </label>
                                </div>
                            )}
                        </div>
                    )}

                    <div>
                        <label className="block text-xs font-medium text-ink-text dark:text-ink-text-dark mb-1">Título do Documento *</label>
                        <input
                            type="text"
                            className="w-full border border-line-strong dark:border-line-dark-strong rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-brand-500 outline-none"
                            placeholder="Ex: CND Federal"
                            value={form.title}
                            onChange={e => setForm({...form, title: e.target.value})}
                        />
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                        <div>
                            <label className="block text-xs font-medium text-ink-text dark:text-ink-text-dark mb-1">Tipo *</label>
                            <select
                                className="w-full border border-line-strong dark:border-line-dark-strong rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-brand-500 outline-none bg-surface dark:bg-surface-dark"
                                value={form.type}
                                onChange={e => setForm({...form, type: e.target.value as any})}
                            >
                                <option value="FEDERAL">Federal</option>
                                <option value="ESTADUAL">Estadual</option>
                                <option value="MUNICIPAL">Municipal</option>
                                <option value="TRABALHISTA">Trabalhista</option>
                                <option value="FGTS">FGTS</option>
                                <option value="FALENCIA">Falência</option>
                                <option value="BALANCO">Balanço</option>
                                <option value="ATESTADO">Atestado</option>
                                <option value="OUTROS">Outros</option>
                            </select>
                        </div>
                        <div>
                             <label className="block text-xs font-medium text-ink-text dark:text-ink-text-dark mb-1">Órgão Emissor</label>
                             <input
                                type="text"
                                className="w-full border border-line-strong dark:border-line-dark-strong rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-brand-500 outline-none"
                                placeholder="Ex: Receita Federal"
                                value={form.issuer}
                                onChange={e => setForm({...form, issuer: e.target.value})}
                            />
                        </div>
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                         <div>
                            <label className="block text-xs font-medium text-ink-text dark:text-ink-text-dark mb-1">Data de Emissão</label>
                            <input
                                type="date"
                                className="w-full border border-line-strong dark:border-line-dark-strong rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-brand-500 outline-none"
                                value={form.issueDate || ''}
                                onChange={e => setForm({...form, issueDate: e.target.value})}
                            />
                        </div>
                        <div>
                            <label className="block text-xs font-medium text-ink-text dark:text-ink-text-dark mb-1">Data de Validade</label>
                            <input
                                type="date"
                                className="w-full border border-line-strong dark:border-line-dark-strong rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-brand-500 outline-none"
                                value={form.expirationDate || ''}
                                onChange={e => setForm({...form, expirationDate: e.target.value})}
                            />
                            <p className="text-[10px] text-ink-placeholder dark:text-ink-placeholder-dark mt-1">Deixe em branco se não expirar.</p>
                        </div>
                    </div>

                    <div>
                        <label className="block text-xs font-medium text-ink-text dark:text-ink-text-dark mb-1">
                            {editingDoc?.file ? 'Substituir Arquivo (PDF/Imagem)' : 'Anexar Arquivo (PDF/Imagem)'}
                        </label>
                        <input
                            type="file"
                            accept=".pdf,.jpg,.jpeg,.png"
                            onChange={handleFileUpload}
                            className="block w-full text-sm text-ink-subtitle dark:text-ink-subtitle-dark file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-sm file:font-semibold file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100 transition-all"
                        />
                        {editingDoc?.file && !selectedFile && (
                            <p className="text-[10px] text-green-600 dark:text-green-300 mt-1 flex items-center gap-1">
                                <CheckCircle className="w-3 h-3" /> Arquivo atual: {editingDoc.file.name}
                            </p>
                        )}
                    </div>
                </div>

                <div className="p-4 bg-surface-alt dark:bg-surface-dark-alt border-t border-line dark:border-line-dark flex justify-end gap-3 flex-shrink-0">
                    <button
                        onClick={() => setIsModalOpen(false)}
                        className="px-4 py-2 text-sm font-medium text-ink-subtitle dark:text-ink-subtitle-dark bg-surface dark:bg-surface-dark border border-line-strong dark:border-line-dark-strong rounded-lg hover:bg-surface-alt dark:bg-surface-dark-alt"
                    >
                        Cancelar
                    </button>
                    <button
                        onClick={handleSave}
                        className="px-4 py-2 text-sm font-medium text-white bg-brand-500 rounded-lg hover:bg-brand-600 flex items-center gap-2 shadow-sm"
                    >
                        <CheckCircle className="w-4 h-4" />
                        {editingDoc ? 'Atualizar Documento' : 'Salvar Documento'}
                    </button>
                </div>
            </div>
        </div>
      )}
    </div>
  );
};

export default DocumentGuide;
